import React, { useState } from 'react';
import { ArrowRight, X, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface Category {
  id: string;
  title: string;
  image: string;
  description: string;
  color: string;
}

const ProductCategories: React.FC = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

  const categories = [
    {
      id: 'birthday',
      title: t('categories.birthday.title') || 'Birthday Cakes',
      image: '/tncakes/BirthdayCakes/BirthdayCakes3.jpeg',
      description: t('categories.birthday.description') || 'Magical birthday cakes that make every celebration unforgettable with personalized designs and flavors',
      color: 'from-pink-500 to-rose-600'
    },
    {
      id: 'wedding',
      title: t('categories.wedding.title') || 'Wedding Cakes',
      image: '/tncakes/WeddingCakes/WeddingCakes2.jpeg',
      description: t('categories.wedding.description') || 'Elegant wedding masterpieces featuring exquisite details, premium ingredients, and romantic designs',
      color: 'from-purple-500 to-pink-600'
    },
    {
      id: 'cupcakes',
      title: t('categories.cupcakes.title') || 'Cupcakes',
      image: '/tncakes/Cupcakes/Cupcakes2.jpeg',
      description: t('categories.cupcakes.description') || 'Artisanal cupcakes with gourmet flavors, beautiful decorations, and perfect individual portions',
      color: 'from-yellow-500 to-orange-600'
    },
    {
      id: 'baby',
      title: t('categories.babyshower.title') || 'Baby Shower Cakes',
      image: '/tncakes/BirthdayCakes/BirthdayCakes4.jpeg', // Fallback to birthday as babyshower is empty
      description: t('categories.babyshower.description') || 'Adorable baby-themed cakes with soft colors, charming designs, and pregnancy-safe ingredients',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      id: 'celebration',
      title: t('categories.celebration.title') || 'Celebration Cakes',
      image: '/tncakes/CelebrationCakes/CelebrationCakes3.jpeg',
      description: t('categories.celebration.description') || 'Custom celebration cakes for graduations, anniversaries, and all your special milestones',
      color: 'from-green-500 to-emerald-600'
    },
    {
      id: 'corporate',
      title: t('categories.corporate.title') || 'Corporate Cakes',
      image: '/tncakes/CorporateCakes/CorporateCakes2.jpeg',
      description: t('categories.corporate.description') || 'Professional corporate cakes with custom branding, elegant presentation for business events',
      color: 'from-gray-600 to-gray-800'
    }
  ];

  return (
    <section className="py-10 md:py-12 bg-emerald-50">
      <div className="container mx-auto px-4">
        {/* Section Header - More Compact */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
            {t('categories.title') || 'Our Specialties'}
          </h2>
          <div className="w-20 h-1 gold-gradient rounded-full mx-auto mb-4"></div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {t('categories.subtitle') || 'Custom cakes designed for every special moment in your life'}
          </p>
        </div>

        {/* Compact Categories Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
          {categories.map((category, index) => (
            <div
              key={index}
              className="group cursor-pointer"
            >
              <div className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
                {/* Compact Image */}
                <div className="relative h-32 md:h-40 overflow-hidden">
                  <div
                    className="h-full w-full bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                    style={{ backgroundImage: `url(${category.image})` }}
                  />
                  {/* Gradient Overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-t ${category.color} opacity-70 group-hover:opacity-50 transition-opacity duration-300`}></div>

                  {/* Info Button */}
                  <button
                    onClick={() => setSelectedCategory(category)}
                    className="absolute top-2 right-2 w-6 h-6 bg-white bg-opacity-80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white"
                  >
                    <Info className="w-3 h-3 text-gray-700" />
                  </button>

                  {/* Title Overlay */}
                  <div className="absolute inset-0 p-3 flex flex-col justify-end text-white">
                    <h3 className="text-sm md:text-base font-serif font-bold text-center leading-tight">
                      {category.title}
                    </h3>
                  </div>
                </div>

                {/* Compact Bottom Info */}
                <Link to={`/portfolio?category=${category.id}`} className="block p-3 bg-white hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600 font-medium">View Collection</span>
                    <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-yellow-500 group-hover:text-white transition-all duration-300">
                      <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Simple Popup Modal */}
        {selectedCategory && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedCategory(null)}
          >
            <div
              className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedCategory(null)}
                className="absolute top-4 right-4 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>

              {/* Category Image */}
              <div className="relative h-48 rounded-xl overflow-hidden mb-4">
                <div
                  className="h-full w-full bg-cover bg-center"
                  style={{ backgroundImage: `url(${selectedCategory.image})` }}
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${selectedCategory.color} opacity-60`}></div>
                <div className="absolute inset-0 flex items-end p-4">
                  <h3 className="text-xl font-serif font-bold text-white">
                    {selectedCategory.title}
                  </h3>
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <p className="text-gray-700 leading-relaxed">
                  {selectedCategory.description}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Link
                  to={`/portfolio?category=${selectedCategory.id}`}
                  className="flex-1 bg-yellow-500 text-white px-4 py-3 rounded-lg font-medium text-center hover:bg-yellow-600 transition-colors"
                  onClick={() => setSelectedCategory(null)}
                >
                  View Collection
                </Link>
                <Link
                  to="/inquiry"
                  className="flex-1 border border-yellow-500 text-yellow-600 px-4 py-3 rounded-lg font-medium text-center hover:bg-yellow-50 transition-colors"
                  onClick={() => setSelectedCategory(null)}
                >
                  Order Now
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductCategories;
